/**
 * Documento da proposta em PDF (D103), no modelo atual da empresa (proposta PORTODAFOLHA-11-2025).
 * Funções puras (sem tela): montam a definição do pdfmake a partir do conteúdo; a geração usa o pdfmake 0.3.
 */
import { formatarCep, formatarCnpj } from "@sobral/dominio";
import { formatarMoeda, formatarNumero } from "./formato";

export interface LicitacaoProposta {
  id: string;
  pessoa_id: string;
  numero: string;
  processo?: string | null;
  modalidade_id?: string | null;
  objeto: string;
  status: string;
  prazo_entrega_dias: number | null;
  prazo_pagamento_dias: number | null;
}

export interface Empresa {
  razao_social: string;
  cnpj: string;
  inscricao_estadual: string | null;
  logradouro: string;
  numero: string;
  complemento: string | null;
  bairro: string;
  municipio: string;
  uf: string;
  cep: string;
  telefone: string | null;
  email: string | null;
  representante_nome: string | null;
  representante_cargo: string | null;
  assinatura_caminho: string | null;
}

export interface Orgao {
  nome: string;
  razao_social: string | null;
  documento: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  municipio: string | null;
  uf: string | null;
  cep: string | null;
}

export interface ModeloProposta {
  id: string;
  declaracoes: string;
  validade_dias: number;
  representante_qualificacao: string | null;
  cidade_assinatura: string;
}

export interface PrecoGravado {
  item_id: string;
  versao: number;
  momento: string | null;
  preco_unit: number;
  produto_id?: string;
  produto: { descricao: string; fabricante: string | null; modelo: string | null } | null;
}

export interface LinhaProposta {
  item: string;
  quantidade: number;
  unidade: string;
  descricao: string;
  marca: string;
  modelo: string;
  valor: number;
  total: number;
}

export type Versao = "inicial" | "readequada";

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
export const dataPorExtenso = (d: Date) => `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;

const NUMEROS: Record<number, string> = { 5: "cinco", 10: "dez", 15: "quinze", 20: "vinte", 30: "trinta", 45: "quarenta e cinco", 60: "sessenta", 90: "noventa", 120: "cento e vinte", 180: "cento e oitenta" };
/** "180 (cento e oitenta) dias" quando o número tem extenso conhecido; senão "180 dias". */
export const diasPorExtenso = (n: number) => `${n}${NUMEROS[n] ? ` (${NUMEROS[n]})` : ""} dias`;

const endereco = (e: Pick<Orgao, "logradouro" | "numero" | "complemento" | "bairro" | "municipio" | "uf" | "cep">) =>
  [
    [e.logradouro, e.numero].filter(Boolean).join(", nº "),
    e.complemento,
    e.bairro,
    [e.municipio, e.uf].filter(Boolean).join("/"),
    e.cep ? `CEP ${formatarCep(e.cep)}` : null,
  ]
    .filter(Boolean)
    .join(" – ");

export async function imagemComoDataUrl(blob: Blob): Promise<string> {
  return new Promise((ok, erro) => {
    const leitor = new FileReader();
    leitor.onload = () => ok(String(leitor.result));
    leitor.onerror = () => erro(new Error("Imagem ilegível."));
    leitor.readAsDataURL(blob);
  });
}


export interface ConteudoPdf {
  empresa: Empresa;
  orgao: Orgao;
  modalidade: string | null;
  licitacao: LicitacaoProposta;
  linhas: LinhaProposta[];
  total: number;
  declaracoes: string[];
  conta: ContaNaProposta | null;
  modelo: ModeloProposta;
  data: Date;
  versao: Versao;
}

/** Conta bancária como sai na proposta. */
export interface ContaNaProposta {
  texto: string;
  pix: string | null;
}

/** Definição do documento (pdfmake) da proposta. Logo e assinatura em data URL (ou null). */
export function documentoProposta(c: ConteudoPdf, logo: string | null, assinatura: string | null) {
  const azul = "#012639";
  const e = c.empresa;
  const enderecoEmpresa = endereco(e);
  const tabela = {
    table: {
      headerRows: 1,
      widths: [28, 34, 38, "*", 52, 58, 56, 62],
      body: [
        ["Item", "Quant.", "Unid.", "Descrição", "Marca", "Modelo", "Valor R$", "Total R$"].map((t) => ({ text: t, bold: true, fillColor: "#F2F4F5" })),
        ...c.linhas.map((l) => [
          l.item,
          { text: formatarNumero(l.quantidade), alignment: "right" },
          l.unidade,
          { text: l.descricao, fontSize: 7.5 },
          l.marca,
          l.modelo,
          { text: formatarMoeda(l.valor), alignment: "right", noWrap: true },
          { text: formatarMoeda(l.total), alignment: "right", noWrap: true },
        ]),
        [{ text: "Total", colSpan: 7, alignment: "right", bold: true }, {}, {}, {}, {}, {}, {}, { text: formatarMoeda(c.total), alignment: "right", bold: true, noWrap: true }],
      ],
    },
    layout: { hLineWidth: () => 0.5, vLineWidth: () => 0.5, hLineColor: () => "#8a979d", vLineColor: () => "#8a979d", paddingTop: () => 2, paddingBottom: () => 2 },
    fontSize: 8,
  };

  const doc = {
    pageSize: "A4",
    pageMargins: [36, 70, 36, 50],
    defaultStyle: { fontSize: 9, color: "#0d2733" },
    header: logo ? { image: logo, width: 160, alignment: "center", margin: [0, 18, 0, 0] } : { text: e.razao_social, alignment: "center", bold: true, margin: [0, 24, 0, 0] },
    footer: (pagina: number, total: number) => ({
      margin: [36, 10, 36, 0],
      columns: [
        { text: [e.telefone, e.email, enderecoEmpresa].filter(Boolean).join("  ·  "), fontSize: 7, color: "#33474f" },
        { text: `${pagina}/${total}`, alignment: "right", fontSize: 7, color: "#33474f", width: 30 },
      ],
    }),
    content: [
      { text: c.versao === "readequada" ? "PROPOSTA DE PREÇOS READEQUADA" : "PROPOSTA DE PREÇOS", alignment: "center", bold: true, fontSize: 12, color: azul, margin: [0, 0, 0, 8] },
      { text: [{ text: "Razão social: ", bold: true }, e.razao_social] },
      { text: [{ text: "CNPJ: ", bold: true }, formatarCnpj(e.cnpj), e.inscricao_estadual ? `   ·   Inscrição estadual: ${e.inscricao_estadual}` : ""] },
      { text: [{ text: "Endereço: ", bold: true }, enderecoEmpresa] },
      { text: [{ text: "Telefone: ", bold: true }, e.telefone ?? "—", "   ·   ", { text: "E-mail: ", bold: true }, e.email ?? "—"], margin: [0, 0, 0, 6] },
      { canvas: [{ type: "line", x1: 0, y1: 0, x2: 523, y2: 0, lineWidth: 1, lineColor: azul }], margin: [0, 0, 0, 6] },
      { text: "DADOS DO PROCESSO", bold: true, color: azul, margin: [0, 0, 0, 3] },
      { text: [{ text: "Órgão: ", bold: true }, c.orgao.razao_social ?? c.orgao.nome] },
      {
        text: [
          { text: "Endereço: ", bold: true },
          endereco(c.orgao) || "—",
          c.orgao.documento && c.orgao.documento.length === 14 ? `   ·   CNPJ ${formatarCnpj(c.orgao.documento)}` : "",
        ],
      },
      { text: [{ text: `${c.modalidade ?? "Licitação"} nº `, bold: true }, c.licitacao.numero, c.licitacao.processo ? `   ·   Processo ${c.licitacao.processo}` : ""] },
      { text: [{ text: "Objeto: ", bold: true }, c.licitacao.objeto], alignment: "justify", margin: [0, 0, 0, 8] },
      { text: "PROPOSTA", bold: true, alignment: "center", color: azul, margin: [0, 0, 0, 4] },
      tabela,
      { text: "", margin: [0, 6, 0, 0] },
      ...c.declaracoes.map((d) => ({ text: d, alignment: "justify", margin: [0, 0, 0, 4], leadingIndent: 18 })),
      ...(c.conta
        ? [
            { text: "DADOS BANCÁRIOS", bold: true, color: azul, margin: [0, 6, 0, 2] },
            { text: `${c.conta.texto}${c.conta.pix ? `   ·   PIX: ${c.conta.pix}` : ""}` },
          ]
        : []),
      ...(c.modelo.representante_qualificacao
        ? [
            { text: "REPRESENTANTE LEGAL", bold: true, color: azul, margin: [0, 6, 0, 2] },
            { text: c.modelo.representante_qualificacao, alignment: "justify" },
          ]
        : []),
      { text: `${c.modelo.cidade_assinatura}, ${dataPorExtenso(c.data)}.`, margin: [0, 14, 0, 6] },
      ...(assinatura ? [{ image: assinatura, width: 140, alignment: "center" }] : [{ text: "\n\n" }]),
      { canvas: [{ type: "line", x1: 160, y1: 0, x2: 363, y2: 0, lineWidth: 0.5 }] },
      { text: e.representante_nome ?? "", alignment: "center", bold: true, margin: [0, 2, 0, 0] },
      { text: e.representante_cargo ?? "", alignment: "center" },
    ],
  };

  return doc;
}

/** Gera o PDF (pdfmake carregado só quando usado). */
export async function pdfDaProposta(doc: unknown): Promise<Blob> {
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([import("pdfmake/build/pdfmake"), import("pdfmake/build/vfs_fonts")]);
  pdfMake.addVirtualFileSystem(vfs);
  return pdfMake.createPdf(doc).getBlob();
}
